import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crosshair, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";
import { useEditor } from "@/components/editor/useEditorState";

const WHERE = { section: "Sezione", component: "Componente condiviso", data: "Dati condivisi (DATA)" };

// Text chooser: list of the element's texts or click on a text in the preview (runtime "pickText" mode).
export default function TextPicker({ target, value, onChange }) {
  const ed = useEditor();
  const { api, id, ui, setUi } = ed;
  const [res, setRes] = useState(null);
  const [q, setQ] = useState("");
  const params = target?.kind === "component" ? { component_id: target.id } : { section_id: target?.id };
  useEffect(() => {
    setRes(null); setQ("");
    if (!target?.id) return;
    api.get(`/projects/${id}/ai-edits/texts`, { params }).then(({ data }) => setRes(data)).catch((e) => toast.error(errMsg(e)));
  }, [target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const m = ed.picked;
    if (!m?.text || !m.id) return;
    ed.setPicked(null);
    const p = m.kind === "component" ? { component_id: m.id } : { section_id: m.id };
    api.get(`/projects/${id}/ai-edits/texts`, { params: { ...p, q: m.text } }).then(({ data }) => {
      if (data.match) onChange({ text: data.match.text, where: data.match.where });
      else toast.warning("Testo non riconosciuto: sceglilo dall'elenco (alcuni testi sono generati dal codice)");
    }).catch((e) => toast.error(errMsg(e)));
  }, [ed.picked]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = (res?.texts || []).filter((t) => !q || t.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-2" data-testid="ai-text-picker">
      <Button size="sm" variant={ui.textPick ? "default" : "outline"} className="w-full h-8" onClick={() => setUi({ textPick: !ui.textPick, selectMode: false })} data-testid="ai-text-pick-btn">
        <Crosshair size={13} className="mr-1.5" />{ui.textPick ? "Clicca un testo nell'anteprima… (annulla)" : "Scegli il testo nell'anteprima"}
      </Button>
      {value && (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-2 text-[12px] text-sky-900" data-testid="ai-text-chosen">
          <p className="text-[10px] uppercase tracking-wider text-sky-700">Testo scelto · {WHERE[value.where]}</p>{value.text}
          {value.where !== "section" && <p className="mt-1 text-[11px] text-amber-700">È usato da più pagine: per modificarlo sblocca gli elementi condivisi.</p>}
        </div>
      )}
      {!target?.id ? <p className="text-[11px] text-slate-500">Seleziona una sezione (o clicca un testo nell'anteprima) per vedere i suoi testi.</p> : !res ? <Loader2 size={14} className="animate-spin text-sky-600" /> : (
        <>
          <div className="relative"><Search size={12} className="absolute left-2 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Cerca tra ${res.texts.length} testi di «${res.name}»`} className="h-8 pl-6 text-xs" data-testid="ai-text-search" /></div>
          <ul className="max-h-44 overflow-auto rounded-md border divide-y bg-white" data-testid="ai-text-list">
            {list.slice(0, 80).map((t, i) => (
              <li key={t}><button onClick={() => onChange({ text: t, where: res.where })} className={`w-full px-2 py-1.5 text-left text-[11.5px] hover:bg-sky-50 ${value?.text === t ? "bg-sky-50 text-sky-900" : "text-slate-700"}`} data-testid={`ai-text-option-${i}`}>{t}</button></li>
            ))}
            {!list.length && <li className="p-2 text-[11px] text-slate-500">Nessun testo.</li>}
          </ul>
        </>
      )}
    </div>
  );
}
